import type { Localization } from "@app/shared/lib/localization";
import { attr, bool, jsonObjectAttr } from "@app/widgets/shared/parseAttr";


export type VendorAppBlockWidgetProps = {
  appSlug: string;
  schoolLinkId: string;
  surfaceKind: string;
  config: Record<string, unknown>;
  height: number;
  showHeader: boolean;
  cssOverride: string;
};

export const parseVendorAppBlockProps = (container: HTMLElement): VendorAppBlockWidgetProps => {
  const cssOverrideRaw = attr(container, "data-css-override");
  let cssOverride = "";
  try { cssOverride = cssOverrideRaw ? decodeURIComponent(cssOverrideRaw) : ""; } catch { cssOverride = ""; }
  return {
    appSlug: attr(container, "data-app-slug"),
    schoolLinkId: attr(container, "data-school-link-id"),
    surfaceKind: attr(container, "data-surface-kind", "cabinet-block"),
    config: jsonObjectAttr(container, "data-config"),
    height: parseInt(attr(container, "data-height", "500"), 10) || 500,
    showHeader: bool(container, "data-show-header"),
    cssOverride,
  };
};

const RUNTIME_SDK = `
<script>
(function () {
  var pending = Object.create(null), rpcSeq = 0, readyCbs = [], ctx = null;
  function nextId() { return 'r' + (++rpcSeq); }
  var queryOptionNames = ['filter', 'sort', 'sortCast', 'desc', 'limit', 'cursorSort', 'cursorDocId'];
  function warnUnknownQueryOptions(method, opts) {
    if (!opts || typeof opts !== 'object') return opts;
    var unknown = Object.keys(opts).filter(function (key) { return queryOptionNames.indexOf(key) < 0; });
    if (unknown.length) console.warn('[xl] ' + method + ': unknown option(s) ' + unknown.join(', ') + ' are ignored. Supported: ' + queryOptionNames.join(', ') + '.');
    return opts;
  }

  function call(kind, args) {
    var id = nextId();
    return new Promise(function (res, rej) {
      pending[id] = { res: res, rej: rej };
      window.parent.postMessage(Object.assign({ kind: kind, rpcId: id }, args || {}), '*');
    });
  }
  window.addEventListener('message', function (e) {
    var m = e.data;
    if (!m || typeof m !== 'object') return;
    if (m.kind === 'xl.ready') {
      ctx = m.context;
      window.xl.user = ctx.user; window.xl.school = ctx.school; window.xl.app = ctx.app;
      window.xl.surface = ctx.surface || null; window.xl.protocolVersion = m.protocolVersion;
      for (var i = 0; i < readyCbs.length; i++) try { readyCbs[i](ctx); } catch (_) {}
      readyCbs = [];
      return;
    }
    if (m.kind === 'xl.rpc.ok' && pending[m.rpcId]) { var p = pending[m.rpcId]; delete pending[m.rpcId]; p.res(m.result); return; }
    if (m.kind === 'xl.rpc.error' && pending[m.rpcId]) { var pe = pending[m.rpcId]; delete pending[m.rpcId]; var err = new Error(m.message || 'RPC error'); err.code = m.code; pe.rej(err); return; }
  });
  function locale() { return (ctx && ctx.school && ctx.school.locale) || 'ru'; }
  function tz() { return (ctx && ctx.school && ctx.school.timezone) || undefined; }
  function xlReady(cb) { if (typeof cb !== 'function') return; if (ctx) cb(ctx); else readyCbs.push(cb); }
  xlReady.then = function (onFulfilled, onRejected) {
    return new Promise(function (resolve) { xlReady(resolve); }).then(onFulfilled, onRejected);
  };
  window.xl = {
    user: null, school: null, app: null, surface: null, protocolVersion: null,
    ready: xlReady,
    format: {
      date: function (d, opts) { var o = opts || {}, hasC = o.weekday || o.year || o.month || o.day; var base = hasC ? { timeZone: tz() } : { dateStyle: 'medium', timeZone: tz() }; return new Intl.DateTimeFormat(locale(), Object.assign(base, o)).format(new Date(d)); },
      time: function (d, opts) { var o = opts || {}, hasC = o.hour || o.minute || o.second; var base = hasC ? { timeZone: tz() } : { timeStyle: 'short', timeZone: tz() }; return new Intl.DateTimeFormat(locale(), Object.assign(base, o)).format(new Date(d)); },
      dateTime: function (d, opts) { return new Intl.DateTimeFormat(locale(), Object.assign({ dateStyle: 'medium', timeStyle: 'short', timeZone: tz() }, opts || {})).format(new Date(d)); },
      number: function (n, opts) { return new Intl.NumberFormat(locale(), opts || {}).format(Number(n)); },
      plural: function (n, forms) { if (!forms) return String(n); var rule = new Intl.PluralRules(locale()).select(Number(n)); return forms[rule] || forms.other || String(n); },
      relativeTime: function (d) { var diff = (new Date(d).getTime() - Date.now()) / 1000; var rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' }); var abs = Math.abs(diff); if (abs < 60) return rtf.format(Math.round(diff), 'second'); if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute'); if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour'); if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day'); if (abs < 86400 * 365) return rtf.format(Math.round(diff / (86400 * 30)), 'month'); return rtf.format(Math.round(diff / (86400 * 365)), 'year'); },
    },
    storage: {
      get: function (collection, docId) { return call('xl.storage.get', { collection: collection, docId: docId }); },
      query: function (collection, opts) { return call('xl.storage.query', Object.assign({ collection: collection }, warnUnknownQueryOptions('xl.storage.query', opts) || {})); },
      put: function () { return Promise.reject(new Error('storage write disabled in published block surface')); },
      delete: function () { return Promise.reject(new Error('storage write disabled in published block surface')); },
    },
    public: {
      query: function (collection, opts) { return call('xl.public.query', Object.assign({ collection: collection }, warnUnknownQueryOptions('xl.public.query', opts) || {})); },
      get: function (collection, docId) { return call('xl.public.get', { collection: collection, docId: docId }); },
      create: function (collection, data) { return call('xl.public.create', { collection: collection, data: data == null ? {} : data }); },
      scenarios: {
        emit: function (eventKind, data) { return call('xl.public.scenarios.emit', { eventKind: eventKind, data: data == null ? {} : data }); },
      },
    },
    learner: {
      courses: function () { return call('xl.learner.courses', {}); },
      lessons: function (opts) { return call('xl.learner.lessons', { courseId: opts && opts.courseId }); },
    },
    config: { submit: function () {}, cancel: function () {} },
    ui: { toast: function () {}, openLink: function (u) { window.open(u, '_blank'); }, resize: function (h) { window.parent.postMessage({ kind: 'xl.ui.resize', height: h }, '*'); } },
  };
  window.parent.postMessage({ kind: 'xl.init' }, '*');
})();
</script>
`;

type SurfaceData = string | { html?: string; css?: string; js?: string };

type VendorBlockViewer = {
  authorized?: boolean;
  contactId?: string;
};

type VendorBlockFrameMessage = {
  kind?: string;
  rpcId?: string;
  height?: number;
  collection?: string;
  docId?: string;
  data?: Record<string, unknown>;
  filter?: Record<string, unknown>;
  limit?: number;
  sort?: string;
  sortCast?: string;
  desc?: boolean;
  cursorSort?: string;
  cursorDocId?: string;
  eventKind?: string;
  courseId?: string;
};

const neutralizeStyleTag = (css: string): string => css.replace(/<\s*\/\s*style/gi, '<\\/style');

function assemble(surface: SurfaceData, overrideCss?: string): string {
  const cssToUse = overrideCss && overrideCss.trim()
    ? overrideCss
    : (typeof surface === 'string' ? '' : (surface.css || ''));
  if (typeof surface === 'string') {
    if (overrideCss && overrideCss.trim()) {
      const stripped = surface.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
      const styleTag = `<style data-source="block-override">${neutralizeStyleTag(overrideCss)}</style>`;
      return /<head\b[^>]*>/i.test(stripped)
        ? stripped.replace(/<head\b[^>]*>/i, (m) => m + styleTag)
        : styleTag + stripped;
    }
    return surface;
  }
  const cssTag = cssToUse ? `<style data-source="${overrideCss ? 'block-override' : 'vendor'}">${neutralizeStyleTag(cssToUse)}</style>` : '';
  const jsTag = surface.js ? `<script>${surface.js}</script>` : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${cssTag}</head><body>${surface.html || ''}${jsTag}</body></html>`;
}

export const initVendorAppBlock = async (container: HTMLElement, props: VendorAppBlockWidgetProps, loc: Localization) => {
  if (container.querySelector('iframe.xl-vendor-block')) return;
  container.innerHTML = '';
  if (!props.appSlug || !props.schoolLinkId) {
    container.innerHTML = `<div style="padding:30px;text-align:center;color:#999;border:1px dashed #ddd;border-radius:8px">${loc.word("VendorAppBlock.notConfigured", { default: "Application is not configured" })}</div>`;
    return;
  }
  if (props.showHeader) {
    const header = document.createElement('div');
    header.style.cssText = 'padding:8px 12px;background:#fafafa;border:1px solid #e5e7eb;border-bottom:0;border-radius:8px 8px 0 0;font-size:13px;font-weight:600;color:#374151';
    header.textContent = `📦 ${props.appSlug}`;
    container.appendChild(header);
  }
  const url = `/api/v1/public/marketplace/site-block?schoolLinkId=${encodeURIComponent(props.schoolLinkId)}&surfaceKind=${encodeURIComponent(props.surfaceKind)}&appSlug=${encodeURIComponent(props.appSlug || '')}`;
  try {
    const response = await (await fetch(url, { credentials: 'include' })).json();
    const error = response?.errors?.[0];
    const block = response?.body;
    if (error || !block?.surface) {
      const message = String(error?.target || '').toLowerCase() === 'surfacekind'
        ? `${loc.word("VendorAppBlock.noSurface", { default: "The application does not provide this block" })}: ${props.surfaceKind}`
        : `${loc.word("VendorAppBlock.notConnected", { default: "Application is not connected to the school" })}: ${props.appSlug}`;
      container.innerHTML += `<div style="padding:20px;text-align:center;color:#dc2626">${message}</div>`;
      return;
    }
    const viewer: VendorBlockViewer = (block.viewer as VendorBlockViewer) || {};
    const html = assemble(block.surface as SurfaceData, props.cssOverride);
    const iframe = document.createElement('iframe');
    iframe.className = 'xl-vendor-block';
    iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox');
    iframe.style.cssText = `width:100%;height:${props.height}px;border:0;display:block;border-radius:${props.showHeader ? '0 0 8px 8px' : '8px'}`;
    iframe.srcdoc = RUNTIME_SDK + html;
    container.appendChild(iframe);
    const publicBase = `/api/v1/public/application-vendor/${encodeURIComponent(block.installId || '')}`;
    const learnerBase = `/api/v1/application-vendor-learner/${encodeURIComponent(block.installId || '')}`;
    const relay = async (requestUrl: string, init: RequestInit, rpcId: string) => {
      try {
        const response: { body?: unknown; errors?: Array<{ type?: string; message?: string }> } = await (await fetch(requestUrl, { credentials: 'include', ...init })).json();
        const failure = response?.errors?.[0];
        if (failure) {
          iframe.contentWindow!.postMessage({ kind: 'xl.rpc.error', rpcId, code: (failure.type || 'UNKNOWN').toUpperCase(), message: failure.message || '' }, '*');
          return;
        }
        iframe.contentWindow!.postMessage({ kind: 'xl.rpc.ok', rpcId, result: response?.body ?? null }, '*');
      } catch (relayError) {
        const reason = relayError instanceof Error ? relayError.message : String(relayError);
        iframe.contentWindow!.postMessage({ kind: 'xl.rpc.error', rpcId, code: 'FETCH', message: reason }, '*');
      }
    };
    const relayPost = (requestUrl: string, payload: unknown, rpcId: string) =>
      relay(requestUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, rpcId);
    const collectionQuery = (message: VendorBlockFrameMessage): string => {
      const params = new URLSearchParams();
      params.set('limit', String(message.limit || 200));
      if (message.filter && Object.keys(message.filter).length > 0) params.set('filter', JSON.stringify(message.filter));
      if (message.sort) params.set('sort', message.sort);
      if (message.sortCast) params.set('sortCast', message.sortCast);
      if (message.desc !== undefined) params.set('desc', String(message.desc));
      if (message.cursorSort) params.set('cursorSort', message.cursorSort);
      if (message.cursorDocId) params.set('cursorDocId', message.cursorDocId);
      return params.toString();
    };
    const onMessage = (e: MessageEvent) => {
      if (e.source !== iframe.contentWindow) return;
      const message = e.data as VendorBlockFrameMessage | null;
      if (!message) return;
      if (message.kind === 'xl.init') {
        iframe.contentWindow!.postMessage({
          kind: 'xl.ready',
          protocolVersion: 1,
          context: {
            user: viewer.authorized
              ? { id: viewer.contactId || '', name: '', email: null, role: 'student' }
              : { id: 'guest', name: '', email: null, role: 'guest' },
            school: { id: block.schoolId || '', name: '', locale: block.locale || 'ru', currency: block.currency || 'RUB', timezone: block.timezone ?? null },
            app: { slug: block.slug || props.appSlug, installId: block.installId, title: block.appTitle || '', settings: block.settings || {}, permissions: (block.permissions as string[]) || [] },
            surface: { kind: props.surfaceKind, config: props.config },
          },
        }, '*');
      } else if (message.kind === 'xl.ui.resize') {
        const reported = Math.max(120, Math.min(Number(message.height) || 0, 6000));
        if (reported) iframe.style.height = `${reported}px`;
      } else if ((message.kind === 'xl.storage.query' || message.kind === 'xl.public.query') && message.rpcId) {
        relay(`${publicBase}/collection/${encodeURIComponent(message.collection || '')}?${collectionQuery(message)}`, {}, message.rpcId);
      } else if ((message.kind === 'xl.storage.get' || message.kind === 'xl.public.get') && message.rpcId) {
        relay(`${publicBase}/collection/${encodeURIComponent(message.collection || '')}/${encodeURIComponent(message.docId || '')}`, {}, message.rpcId);
      } else if (message.kind === 'xl.public.create' && message.rpcId) {
        relayPost(`${publicBase}/collection/${encodeURIComponent(message.collection || '')}`, { data: message.data || {} }, message.rpcId);
      } else if (message.kind === 'xl.public.scenarios.emit' && message.rpcId) {
        relayPost(`${publicBase}/scenario/emit`, { eventKind: message.eventKind, studentId: viewer.contactId, data: message.data || {} }, message.rpcId);
      } else if (message.kind === 'xl.learner.courses' && message.rpcId) {
        relay(`${learnerBase}/course`, {}, message.rpcId);
      } else if (message.kind === 'xl.learner.lessons' && message.rpcId) {
        relay(`${learnerBase}/lesson?courseId=${encodeURIComponent(message.courseId || '')}`, {}, message.rpcId);
      }
    };
    window.addEventListener('message', onMessage);
  } catch (error: any) {
    container.innerHTML += `<div style="padding:20px;text-align:center;color:#dc2626">${loc.word("VendorAppBlock.loadError", { default: "Loading error" })}: ${error.message || error}</div>`;
  }
};
